import queryKeys from "@configs/react-query/query-key";
import { getSbomTypeLabel, SBOM_TYPE_OPTIONS, isNonEditableSbom } from "@constants/sbom";
import useSuccessToast, { showErrorToast } from "@configs/react-toastify/snackbar";
import { useTheme } from "@hooks/useTheme";
import { useContext, useEffect, useState, useRef, useCallback  } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PermissionContext } from "@contexts/PermissionContext";
import { formatDateForRequest, formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useMutationData } from "@hooks/useMutationData";
import { ConfirmDeleteModal } from "@components/UI";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";

import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { TOAST } from "@constants/index";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

function SbomList() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { canEditSbom } = useContext(PermissionContext);

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState({});
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.SBOM_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [sbomList, setSbomList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isSbomListLoading, setIsSbomListLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { creatorList, fetchSearchInfo } = useSearchInfo();

  useEffect(() => {
    fetchData();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Search fields for advanced search
  const searchFields = [
    {
      name: "sbomTypeCd",
      label: t("sbom.sbom-type"),
      type: "select",
      options: SBOM_TYPE_OPTIONS,
      placeholder: t("sbom.select-sbom-type"),
    },
    {
      name: "createrId",
      label: t("system.creator"),
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
      placeholder: t("system.creator-placeholder"),
    },
    {
      name: "createdAtFrom",
      label: t("system.creation-date-from"),
      placeholder: t("system.creation-date-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("system.creation-date-to"),
      placeholder: t("system.creation-date-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);


  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  // Fetch data from IndexedDB
  const getListData = async (bodyRequest = {}) => {
    setIsSbomListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.SBOMS, {
        pageSize,
        startRow,
        filters,
      });

      if (response && typeof response !== "string") {
        setSbomList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching SBOM list from IndexedDB:", error);
      showErrorToast(t("sbom.fetch-error"));
    } finally {
      setIsSbomListLoading(false);
    }
  };

  const deleteSboms = useMutationData(
    queryKeys.deleteSboms,
    { method: "DELETE" },
    {
      onSuccess: async (response) => {
        await deleteDataInIndexedDb(STORES.SBOMS, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);

        useSuccessToast(TOAST.success, t("common.success"), t("sbom.delete-success"));
      },
      onError: (error) => {
        useSuccessToast(TOAST.error, t("common.error"), t("sbom.delete-error"));
      },
    },
  );
  

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);
  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);


  const handleAddSbom = () => {
    navigate("/sbom/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.sbomSeq}`);
  };

  const handleDeleteSbom = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      // Implement actual delete API call here
      const bodyRequest = {
        ids: selectedRows,
      };
      await deleteSboms.mutateAsync(bodyRequest);

      // Close modal
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting sboms:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSbomToDelete(null);
  };

  const columns = ["sbomName","remark" ,"sbomType", "creator", "createdAt"];

  const columnConfig = {
    sbomName: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      truncate: true,
      headerRender: () => t("sbom.sbom-name"),
      customRender: (value, row) => row.sbomName || "",
    },
    remark: {
      style: {
        width: "50%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      truncate: true,
      headerRender: () => t("sbom.remark"),
      customRender: (value, row) => row.remark || "",
    },
    sbomType: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("sbom.sbom-type"),
      customRender: (value, row) => row.sbomTypeCdName || getSbomTypeLabel(row.sbomTypeCd) || "",
    },
    creator: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("system.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.create-at"),
      customRender: (value, row) => formatDateTime(row.createdAt) || "",
    },
  };

 const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="sbom-list-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}>{t("sbom.management-title")}</h1>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          isAdvancedPanelOpen={showAdvancedSearch}
          onDeleteProject={handleDeleteSbom}
          onAdd={handleAddSbom}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("sbom.add-new-sbom")}
          titleDeleteButton={t("sbom.delete-sbom")}
          isDisplayAddButton={canEditSbom()}
          isDisplayDeleteButton={canEditSbom()}
          searchInputTestId="sbom-list-search-input"
          addButtonTestId="sbom-list-add-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="sbom-list-advanced-search">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="sbom-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={sbomList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isSbomListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={canEditSbom()}
          hasRadioButton={false}
          rowKey="sbomSeq"
          shouldShowGoToPage={true}
          isCheckboxDisabled={isNonEditableSbom}
          showItemsPerPageSelector={true}
        />
      </div>

      <ConfirmDeleteModal
        data-testid="sbom-list-delete-modal"
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("sbom.delete-sbom-confirmation-title")}
        description={t("sbom.delete-sbom-confirmation-description", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  )
}

export default SbomList;
